import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { makeStyles, Grid, Typography } from "@material-ui/core";

import { PAGES } from "../../";
import { KPIS } from "../constants";
import { Toggle } from "../../../components/toggle";
import { PlusButton } from "../../../components/base";
import { BarChart } from "../../../components/charts";
import { NoDataCard } from "../../../components/parts";
import { powerRevenueLegends, powerRevenueBars } from "../sample";
import { ColorIndicator } from "../../../components/colorIndicator";
import { BAR_CHART_COLORS, CHART_KEYS, GRAPH_UNITS } from "../constants";
import {
  api,
  getPercent,
  getLastBarTotal,
  currencyFormatter,
  requestDateFormat,
  nKilowattFormatter,
  sumFromListOfObjects,
  generateEstimatedData,
  isResponseDataPerMonth,
} from "../../../services";
import useDiff from "../../../hooks/useDiff";

const useStyles = makeStyles((theme) => ({
  container: {
    height: "80%",
    paddingLeft: theme.spacing(4),
  },
  row: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    "& p": {
      fontSize: 12,
    },
  },
  title: {
    marginBottom: theme.spacing(1.5),
  },
}));

export const PowerRevenueChart = ({ activeKpi, period, startDate, endDate }) => {
  const classes = useStyles();
  const navigate = useNavigate();
  const [graphUnit, setGraphUnit] = useState(GRAPH_UNITS.kwh);
  const [data, setData] = useState([]);
  const [rawData, setRawData] = useState([]);
  const [legends, setLegends] = useState([]);
  const [bars, setBars] = useState(powerRevenueBars);
  const [diffDays] = useDiff(startDate, endDate);
  const currencySymbol = useSelector((state) => state.auth.user.currency.symbol);

  const handleClick = () => {
    navigate(`${PAGES.dashboard.index}/${PAGES.dashboard.powerRevenues}`);
  };

  const getLegentValueDisplay = (value, graphUnit) => {
    if (graphUnit === GRAPH_UNITS.kwh) {
      return nKilowattFormatter(value);
    }

    return currencyFormatter(value, currencySymbol);
  };

  useEffect(() => {
    const legends = powerRevenueLegends.map((legend) => ({
      ...legend,
      value: 0,
      percentage: 0,
    }));

    const isDataPerMonth = isResponseDataPerMonth(rawData);
    const currentYear = new Date().getUTCFullYear();

    const data = rawData.map((item) => {
      const trading = graphUnit === GRAPH_UNITS.kwh ? item.trading_kwh : item.trading;
      const battery = graphUnit === GRAPH_UNITS.kwh ? item.battery_kwh : item.battery;
      const ev = graphUnit === GRAPH_UNITS.kwh ? item.ev_kwh : item.ev;

      const date = new Date(item.date);
      const year = date.getUTCFullYear();
      if (year > currentYear) {
        return {
          date: item.date,
          estimated: trading + battery + ev,
          x: `${isDataPerMonth ? `${date.getMonth() + 1}-` : ""}${date.getUTCFullYear()}-E*`,
        };
      }

      return {
        date: item.date,
        kwh: item.kwh,
        power_revenue: item.power_revenue,
        trading: trading,
        battery: battery,
        ev: ev,
        x: `${isDataPerMonth ? `${date.getMonth() + 1}-` : ""}${date.getUTCFullYear()}`,
      };
    });

    const fields = ["trading", "battery", "ev"];
    fields.forEach((item, i) => {
      legends[i].value = sumFromListOfObjects(data, item);
      legends[i].valueDisplay = getLegentValueDisplay(legends[i].value, graphUnit);
    });

    const fieldName = graphUnit === GRAPH_UNITS.kwh ? "kwh" : "power_revenue";

    const total = sumFromListOfObjects(data, fieldName);
    legends.forEach((legend) => {
      let percentage = getPercent(legend.value, total);
      legend.percentage = percentage;
    });

    let tempBars = [...powerRevenueBars];
    if (graphUnit === GRAPH_UNITS.kwh) {
      tempBars = tempBars.map((bar) => ({ ...bar, unit: "kwh" }));
    }
    setBars(tempBars);
    setLegends(legends);

    const lastBarTotal = getLastBarTotal(data, ["trading", "battery", "ev"]);
    const estimatedData = generateEstimatedData(data, lastBarTotal, isDataPerMonth, graphUnit);
    setData(data.concat(estimatedData));
  }, [rawData, graphUnit]);

  useEffect(() => {
    if (activeKpi === KPIS.power_revenue) {
      const getData = (url) => {
        api.get(url).then((resp) => setRawData(resp.data));
      };

      let url = "/praas/v1/dashboard/power-revenue-graph/";
      if (period.name === "RANGE" && endDate) {
        url += `?from_date=${requestDateFormat(startDate)}&to_date=${requestDateFormat(endDate)}`;
        getData(url);
      } else if (["YTD", "Acc.", "20Y"].includes(period.name)) {
        url += `?date_filter=${period.filter}`;
        getData(url);
      }
    }
  }, [activeKpi, period, startDate, endDate]);

  return (
    <>
      <Grid className={classes.title} container alignItems="center">
        <Typography
          style={{
            fontWeight: "bold",
            marginRight: 25,
          }}
        >
          Power revenue
        </Typography>
        <Toggle
          value={graphUnit}
          options={[currencySymbol, GRAPH_UNITS.kwh]}
          onChanged={(unit) => setGraphUnit(unit)}
        />
      </Grid>
      {rawData.length ? (
        <Grid container>
          <Grid item xs={9} xl={10}>
            <BarChart
              chartData={data}
              bars={bars}
              period={period}
              dateDiff={diffDays}
              unit={graphUnit}
            />
          </Grid>
          <Grid item xs={3} xl={2}>
            <Grid
              className={classes.container}
              container
              alignItems="center"
              justifyContent="space-between"
            >
              <Grid item xs={10}>
                {legends.map((legend, index) => (
                  <Grid
                    className={classes.row}
                    key={index}
                    container
                    alignItems="center"
                    justifyContent="space-evenly"
                  >
                    <Grid item xs={5}>
                      <Grid container alignItems="center">
                        <ColorIndicator
                          color={BAR_CHART_COLORS[legend.key]}
                          onlyBorder={legend.key === CHART_KEYS.total_unrealized}
                        />
                        <Typography>{legend.text}</Typography>
                      </Grid>
                    </Grid>
                    <Grid item xs={5}>
                      <Typography>{legend.valueDisplay}</Typography>
                    </Grid>
                    <Grid item xs={2}>
                      <Typography>{legend.percentage}%</Typography>
                    </Grid>
                  </Grid>
                ))}
              </Grid>
              <Grid item xs={2}>
                <PlusButton onClick={handleClick} />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      ) : (
        <Grid>
          <NoDataCard rootStyle={{ minHeight: 200 }} gridStyle={{ height: 120 }} />
        </Grid>
      )}
    </>
  );
};
